import { useCallback, useEffect, useState } from "react";

import { UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { Consumptions, ConsumptionsStructure } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $ok } from "foundation-ts/commons";
import { Nullable, UUID } from "foundation-ts/types";

import { exportCorporationConsumptions, getCorporationConsumptions, getCorporationCurrentConsumption } from "@services/corporations.service";
import { exportConsumptions, getConsumptions, getCurrentConsumption } from "@services/stats.service";

export function useConsumptions(connectedUser: Nullable<UserDto>, scope: ConsumptionScope) {
    const [nbFrees, setNbFrees] = useState(0);
    const [nbPaid, setNbPaid] = useState(0);
    const [nbRefused, setNbRefused] = useState(0);
    const [inProgress, setInProgress] = useState(0);
    const [lastyearConsumptions, setLastYearConsumptions] = useState<Nullable<ConsumptionsStructure>>(null);

    const exportFileConsumptions = useCallback(
        async () => {
            switch (scope.type) {
                case "all":
                    await exportConsumptions();
                    break;
                case "corporation":
                    await exportCorporationConsumptions(scope.corporationId!, scope.corporationName!);
                    break;
                case "user":
                    await exportConsumptions(scope.userId, scope.username);
                    break;
                default:
                    await exportConsumptions(connectedUser?.apid);
                    break;
            }
        },
        [scope]
    );

    useEffect(() => {
        if (!$ok(connectedUser)) { return; }

        let cancelled = false;
        const loadCurrentConsumptions = async () => {
            let consumptions: Nullable<Consumptions>;
            switch (scope.type) {
                case "all":
                    consumptions = await getCurrentConsumption();
                    break;
                case "corporation":
                    if (!$ok(scope.corporationId)) { return; }
                    consumptions = await getCorporationCurrentConsumption(scope.corporationId!);
                    break;
                case "user":
                    if (!$ok(scope.userId)) { return; }
                    await getCurrentConsumption(scope.userId!);
                    break;
                default:
                    consumptions = await getCurrentConsumption(connectedUser?.apid);
                    break;
            }

            if (cancelled) { return; }
            setNbFrees($ok(consumptions) ? consumptions!.signed.qualified : 0);
            setNbPaid(
                $ok(consumptions)
                    ? consumptions!.signed.emailOTP + (consumptions?.signed.smsOTP ?? 0)
                    : 0
            );
            setNbRefused(consumptions?.refused ?? 0);
            setInProgress(consumptions?.inprogress ?? 0);
        };

        const loadLastYearConsumptions = async () => {
            let consumptions: Nullable<ConsumptionsStructure>;
            switch (scope.type) {
                case "all":
                    consumptions = await getConsumptions();
                    break;
                case "corporation":
                    if (!$ok(scope.corporationId)) { return; }
                    consumptions = await getCorporationConsumptions(scope.corporationId!);
                    break;
                case "user":
                    if (!$ok(scope.userId)) { return; }
                    consumptions = await getConsumptions(scope.userId!);
                    break;
                default:
                    consumptions = await getConsumptions(connectedUser?.apid);
                    break;
            }

            if (cancelled) { return; }
            setLastYearConsumptions(consumptions);
        };

        loadCurrentConsumptions();
        loadLastYearConsumptions();
        return () => { cancelled = true; };
    }, [connectedUser, scope]);

    return { nbFrees, nbPaid, nbRefused, inProgress, lastyearConsumptions, exportFileConsumptions };
}

export type ConsumptionScope = { type: "mine" } | { type: "all" } | { type: "user", userId?: UUID, username?: string } | { type: "corporation", corporationId?: UUID, corporationName?: string };